import React, { useState, useEffect, useCallback } from "react";
import Loader from './Loader';
import { toast } from 'react-toastify';
import ApiService from '../services/ApiService';
import Swal from 'sweetalert2';
import { confirmDeleteBooking } from '../utils/bookingActions';
import { hasPermission } from '../utils/permissions';
import { formatDate, formatCurrency } from '../utils/helper';
import { previewImageOnClick, previewCursor } from '../utils/imagePreview';
import './BookingDetailsSidebar.css';

const GOLD = '#4F97D8';

const STATUS_ACTIONS = {
  pending: [
    { value: 'Confirmed', label: 'Confirm Booking', icon: 'bi-check-circle', cls: 'bd-btn-primary' },
    { value: 'Cancelled', label: 'Cancel Booking',  icon: 'bi-x-circle',     cls: 'bd-btn-danger'  },
  ],
  // Once its scheduled time arrives, a confirmed booking is started (below,
  // gated on bookingHasStarted) rather than completed directly — Complete only
  // becomes available after the session has actually been started.
  confirmed: [
    { value: 'In Progress', label: 'Start Session', icon: 'bi-play-circle', cls: 'bd-btn-primary' },
    { value: 'Cancelled',   label: 'Cancel Booking', icon: 'bi-x-circle',   cls: 'bd-btn-danger'  },
  ],
  'in progress': [
    { value: 'Completed', label: 'Mark as Complete', icon: 'bi-patch-check', cls: 'bd-btn-primary' },
    { value: 'Cancelled', label: 'Cancel Booking',   icon: 'bi-x-circle',    cls: 'bd-btn-danger'  },
  ],
};

// Booking start as a Date, combining the stored date with its "HH:MM" (24h) or
// "h:mm AM/PM" (12h) time. Returns null when there is no date to anchor to.
const parseBookingStart = (b) => {
  if (!b?.booking_date) return null;
  const start = new Date(b.booking_date);
  if (Number.isNaN(start.getTime())) return null;

  const raw = (b.booking_time || '').trim();
  const m12 = raw.match(/^(\d{1,2}):(\d{2})\s*([AaPp][Mm])$/);
  const m24 = raw.match(/^(\d{1,2}):(\d{2})$/);
  let mins = null;
  if (m12) {
    let h = parseInt(m12[1], 10) % 12;
    if (/[Pp]/.test(m12[3])) h += 12;
    mins = h * 60 + parseInt(m12[2], 10);
  } else if (m24) {
    mins = parseInt(m24[1], 10) * 60 + parseInt(m24[2], 10);
  }

  start.setHours(0, 0, 0, 0);
  if (mins != null) start.setMinutes(mins);
  return start;
};

// True once the scheduled booking time has started. Unknown/undateable bookings
// default to true so they are never permanently un-completable.
const bookingHasStarted = (b) => {
  const start = parseBookingStart(b);
  return start ? Date.now() >= start.getTime() : true;
};

const statusPillClass = (status) => {
  const map = {
    pending:   'bd-status-pill bd-pending',
    confirmed: 'bd-status-pill bd-confirmed',
    completed: 'bd-status-pill bd-completed',
    cancelled: 'bd-status-pill bd-cancelled',
    refunded:  'bd-status-pill bd-refunded',
    active:    'bd-status-pill bd-confirmed',
    late:      'bd-status-pill bd-late',
    'no show': 'bd-status-pill bd-no-show',
    'in progress': 'bd-status-pill bd-confirmed',
  };
  return map[status?.toLowerCase()] || 'bd-status-pill bd-default';
};

const BookingDetailsSidebar = ({ bookingId, selectedBooking, onUpdate, onDelete }) => {
  const [isLoader, setIsLoader] = useState(false);
  const [booking, setBooking] = useState(null);

  // Tracking is optional/collapsed by default — fetched only the first time
  // the admin actually opens the tab, not on every booking load.
  const [trackingOpen, setTrackingOpen] = useState(false);
  const [tracking, setTracking] = useState(null); // null = not fetched yet
  const [trackingLoading, setTrackingLoading] = useState(false);

  const getBookingData = useCallback(async () => {
    if (!bookingId) return;
    try {
      setIsLoader(true);
      const res = await ApiService.request({ method: 'GET', url: `admin/bookings/${bookingId}` });
      if (res.data.status) setBooking(res.data.data);
      else toast.error(res.data.message);
    } catch {
      toast.error('Failed to load booking details');
    } finally {
      setIsLoader(false);
    }
  }, [bookingId]);

  useEffect(() => {
    if (bookingId) getBookingData();
    else if (selectedBooking) setBooking(selectedBooking);
    // A different booking means stale tracking for the old one — collapse
    // and drop it so opening the tab always fetches fresh.
    setTrackingOpen(false);
    setTracking(null);
  }, [bookingId, selectedBooking, getBookingData]);

  const fetchTracking = async (id) => {
    try {
      setTrackingLoading(true);
      const res = await ApiService.request({ method: 'GET', url: `admin/bookings/${id}/tracking` });
      setTracking(res.data.status ? (res.data.data || []) : []);
    } catch {
      toast.error('Failed to load booking tracking');
      setTracking([]);
    } finally {
      setTrackingLoading(false);
    }
  };

  const toggleTracking = () => {
    const willOpen = !trackingOpen;
    setTrackingOpen(willOpen);
    if (willOpen && tracking === null && booking?._id) fetchTracking(booking._id);
  };

  const handleStatusUpdate = async (newStatus) => {
    const b = booking;
    if (!b?._id) { toast.error('Booking not found'); return; }

    // Cancelling asks for a reason: it reaches the customer in the cancellation
    // notification and stays on the booking record, so it cannot be left blank.
    const isCancelling = String(newStatus).toLowerCase() === 'cancelled';
    const isStarting = String(newStatus).toLowerCase() === 'in progress';

    const result = await Swal.fire({
      title: isCancelling ? 'Cancel Booking' : isStarting ? 'Start Session' : `Mark as ${newStatus}`,
      text: isCancelling
        ? undefined
        : isStarting
          ? 'Start this session now?'
          : `Update this booking status to ${newStatus}?`,
      icon: isCancelling ? 'warning' : 'question',
      input: isCancelling ? 'textarea' : undefined,
      inputLabel: isCancelling ? 'Reason for cancellation' : undefined,
      inputPlaceholder: isCancelling ? 'This is shown to the customer…' : undefined,
      inputAttributes: isCancelling ? { maxlength: 300 } : undefined,
      inputValidator: isCancelling
        ? (value) => (!value || !value.trim() ? 'A cancellation reason is required' : undefined)
        : undefined,
      showCancelButton: true,
      confirmButtonColor: isCancelling ? '#d33' : GOLD,
      confirmButtonText: isCancelling ? 'Yes, cancel booking' : isStarting ? 'Yes, start session' : `Yes, ${newStatus}`,
      cancelButtonText: 'Cancel',
    });
    if (!result.isConfirmed) return;

    try {
      setIsLoader(true);
      const res = await ApiService.request({
        method: 'POST',
        url: `admin/bookings/${b._id}/status`,
        data: {
          status: newStatus,
          payment_status: b.payment_status,
          notes: b.notes || '',
          ...(isCancelling && { cancellation_reason: result.value.trim() }),
        },
      });
      if (res.data.status) {
        toast.success(isStarting ? 'Session started' : `Booking marked as ${newStatus}`);
        try {
          const full = await ApiService.request({ method: 'GET', url: `admin/bookings/${b._id}` });
          const updated = full.data.status ? full.data.data : res.data.data;
          setBooking(updated);
          onUpdate?.(updated);
        } catch {
          setBooking(res.data.data);
          onUpdate?.(res.data.data);
        }
        // The status change just added a row — refresh it if the tab is
        // already open, otherwise leave it uncached for the next open.
        if (trackingOpen) fetchTracking(b._id); else setTracking(null);
      } else {
        toast.error(res.data.message || 'Failed to update status');
      }
    } catch (err) {
      toast.error('Failed to update booking status');
      console.error(err);
    } finally {
      setIsLoader(false);
    }
  };

  // Only offered when the host page handles deletion (passes onDelete).
  const canDelete = !!onDelete && hasPermission('bookings.delete');

  const handleDelete = async () => {
    setIsLoader(true);
    const deleted = await confirmDeleteBooking(booking);
    setIsLoader(false);
    if (deleted) onDelete(booking);
  };

  const fmt = (amount) => `AED ${Number(amount ?? 0).toLocaleString()}`;

  const b = booking;
  // A session cannot be started before its scheduled time, so the Start Session
  // action is withheld until then (Cancel and others stay available). Complete
  // then only becomes available once a session has actually been started.
  const started = bookingHasStarted(b);
  // A walk-in/guest booking (created from the Admin Booking Calendar) has no
  // Cancel option — the client is already present when it's created, so
  // there's nothing to cancel.
  const isWalkIn = b?.booking_source === 'admin';
  const allActions = STATUS_ACTIONS[b?.status?.toLowerCase()] || [];
  const currentActions = allActions.filter((a) => a.value !== 'In Progress' || started)
    .filter((a) => a.value !== 'Cancelled' || !isWalkIn);
  // Explain the missing Start Session button while the booking is still upcoming.
  const completeWithheld = !started && allActions.some((a) => a.value === 'In Progress');
  const startLabel = completeWithheld
    ? formatDate(parseBookingStart(b), 'MMM DD, YYYY h:mm A')
    : '';

  if (isLoader && !b) {
    return (
      <div className="d-flex justify-content-center align-items-center py-10">
        <Loader text="Loading..." size={35} />
      </div>
    );
  }

  if (!b) return null;

  const bookingCode = b.booking_code || `#${b._id?.toString().slice(-8)}`;
  const customerName = b.user_id?.name || b.guest_name || 'N/A';
  const services = b.services || [];
  const bookingDateTime = b.booking_date
    ? `${formatDate(b.booking_date, 'MMM DD, YYYY')}${b.booking_time ? ` at ${b.booking_time}` : ''}`
    : 'N/A';

  const copyCode = () => {
    navigator.clipboard?.writeText(bookingCode);
    toast.success('Booking ID copied', { autoClose: 1500 });
  };

  return (
    <div className="booking-sidebar-wrapper">
      {isLoader && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.15)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Loader text="Processing" size={40} />
        </div>
      )}

      {/* Status + code */}
      <div className="bd-top-row">
        <span className={`${statusPillClass(b.status)} text-uppercase`}>{b.status || 'N/A'}</span>
        <button className="bd-code-btn" onClick={copyCode} title="Copy booking ID">
          {bookingCode}
          <i className="bi bi-clipboard"></i>
        </button>
      </div>

      {/* Customer (or walk-in guest, for an admin-created manual booking) */}
      <div className="bd-customer">
        <div className="bd-customer-avatar">
          {b.user_id?.picture || b.user_id?.image ? (
            <img
              src={b.user_id.picture || b.user_id.image}
              alt={b.user_id?.name}
              style={{ cursor: previewCursor(b.user_id.picture || b.user_id.image) }}
              onClick={(e) => previewImageOnClick(e, customerName)}
              onError={(e) => { e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(customerName)}&background=E3F0FC&color=4F97D8&size=128`; }}
            />
          ) : (
            <img src={`https://ui-avatars.com/api/?name=${encodeURIComponent(customerName)}&background=E3F0FC&color=4F97D8&size=128`} alt={customerName} />
          )}
        </div>
        <div className="bd-customer-info">
          <div className="bd-customer-name">
            {customerName}
            {!b.user_id && <span className="badge badge-light-warning ms-2">Walking Customer</span>}
          </div>
          <div className="bd-customer-line"><i className="bi bi-telephone"></i>{b.user_id?.phone || b.guest_phone || 'N/A'}</div>
          {b.user_id && <div className="bd-customer-line"><i className="bi bi-envelope"></i>{b.user_id?.email || 'N/A'}</div>}
        </div>
      </div>

      {/* Booking Information */}
      <div className="bd-section">
        <h6 className="bd-section-title">Booking Information</h6>
        <div className="bd-row">
          <span className="bd-label">Room</span>
          <span className="bd-value text-capitalize">{b.room_id?.title || 'N/A'}</span>
        </div>
        <div className="bd-row">
          <span className="bd-label">Date &amp; Time</span>
          <span className="bd-value">{bookingDateTime}</span>
        </div>
        <div className="bd-row">
          <span className="bd-label">Booked On</span>
          <span className="bd-value">{b.createdAt ? formatDate(b.createdAt, 'MMM DD, YYYY h:mm A') : 'N/A'}</span>
        </div>
      </div>

      {/* Sessions — each entry is a service booked into a room for a duration */}
      <div className="bd-section">
        <h6 className="bd-section-title">Sessions</h6>
        {services.length > 0 ? services.map((s, i) => (
          <div className="bd-row" key={i}>
            <span className="bd-label">{s.service_id?.title || 'Service'}</span>
            <span className="bd-value">
              {s.duration ? `${s.duration} mins` : ''}
            </span>
          </div>
        )) : (
          <div className="bd-row">
            <span className="bd-value">No sessions listed</span>
          </div>
        )}
      </div>

      {/* Payment */}
      <div className="bd-section">
        <h6 className="bd-section-title">Payment</h6>
        <div className="bd-row">
          <span className="bd-label">Amount</span>
          <span className="bd-value fw-bold">{formatCurrency(b.grand_total)}</span>
        </div>
        <div className="bd-row">
          <span className="bd-label">Pay By</span>
          <span className="bd-value text-capitalize">
            {b.payment_by?.replace(/_/g, ' ') || 'N/A'}
          </span>
        </div>
        <div className="bd-row">
          <span className="bd-label">Payment Method</span>
          <span className="bd-value text-capitalize">
            {b.payment_method?.replace(/_/g, ' ') || 'N/A'}
          </span>
        </div>
        <div className="bd-row">
          <span className="bd-label">Payment Status</span>
          <span className={`bd-pay-pill ${String(b.payment_status).toLowerCase() === 'paid' ? 'bd-paid' : 'bd-unpaid'}`}>
            {b.payment_status || 'N/A'}
          </span>
        </div>
        {b.transaction_id && (
          <div className="bd-row">
            <span className="bd-label">Transaction ID</span>
            <span className="bd-value">{b.transaction_id}</span>
          </div>
        )}
      </div>

      {/* Tracking — who created the booking and every status change since,
          with a timestamp for each. Collapsed by default; fetched only the
          first time the admin actually opens it. */}
      <div className="bd-section">
        <button type="button" className="bd-tracking-toggle" onClick={toggleTracking}>
          <h6 className="bd-section-title mb-0">Tracking</h6>
          <i className={`bi bi-chevron-${trackingOpen ? 'up' : 'down'}`}></i>
        </button>
        {trackingOpen && (
          trackingLoading ? (
            <div className="bd-tracking-loading"><Loader text="Loading" size={20} /></div>
          ) : tracking && tracking.length > 0 ? (
            <div className="bd-tracking-list">
              {tracking.map((row) => (
                <div className="bd-tracking-row" key={row._id}>
                  <div className="bd-tracking-row-top">
                    <span className="bd-tracking-action">
                      {row.action === 'created' ? 'Created' : `${row.previous_status || '—'} → ${row.status}`}
                    </span>
                    <span className="bd-tracking-time">{formatDate(row.createdAt, 'MMM DD, YYYY h:mm A')}</span>
                  </div>
                  <div className="bd-tracking-row-bottom">
                    <span className="bd-tracking-by">
                      <i className={`bi ${row.performed_by_type === 'admin' ? 'bi-person-badge' : row.performed_by_type === 'user' ? 'bi-person' : 'bi-gear'} me-1`}></i>
                      {row.performed_by_type === 'admin'
                        ? (row.performed_by_admin_id?.name || 'Admin')
                        : row.performed_by_type === 'user'
                          ? (row.performed_by_user_id?.name || 'Customer')
                          : 'System'}
                    </span>
                    {row.note && <span className="bd-tracking-note">{row.note}</span>}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="bd-row"><span className="bd-value">No tracking recorded</span></div>
          )
        )}
      </div>

      {/* Cancellation — only once the booking has actually been cancelled */}
      {b.cancelled_at && (
        <div className="bd-section">
          <h6 className="bd-section-title">Cancellation</h6>
          <div className="bd-row">
            <span className="bd-label">Cancelled On</span>
            <span className="bd-value">{formatDate(b.cancelled_at, 'MMM DD, YYYY')}</span>
          </div>
          <div className="bd-row">
            <span className="bd-label">Cancelled By</span>
            <span className="bd-value text-capitalize">{b.cancelled_by || 'N/A'}</span>
          </div>
          {b.cancellation_reason && (
            <div className="bd-row">
              <span className="bd-label">Reason</span>
              <span className="bd-value">{b.cancellation_reason}</span>
            </div>
          )}
          <div className="bd-row">
            <span className="bd-label">Cancellation Charge</span>
            <span className="bd-value bd-danger">{fmt(b.cancellation_charge)}</span>
          </div>
          <div className="bd-row">
            <span className="bd-label">Refund Amount</span>
            <span className="bd-value bd-success">{fmt(b.refund_amount)}</span>
          </div>
        </div>
      )}

      {/* Actions */}
      {(currentActions.length > 0 || canDelete) && (
        <div className="bd-actions">
          <h6 className="bd-section-title">Actions</h6>
          {completeWithheld && (
            <p className="bd-action-note">
              <i className="bi bi-clock-history me-2"></i>
              This session can be started once its scheduled time
              {startLabel ? ` (${startLabel})` : ''} has passed.
            </p>
          )}
          {currentActions.map(({ value, label, icon, cls }) => (
            <button
              key={value}
              className={`bd-action-btn ${cls}`}
              disabled={isLoader}
              onClick={() => handleStatusUpdate(value)}
            >
              <i className={`bi ${icon} me-2`}></i>{label}
            </button>
          ))}
          {canDelete && (
            <button
              className="bd-action-btn bd-btn-danger"
              disabled={isLoader}
              onClick={handleDelete}
            >
              <i className="bi bi-trash me-2"></i>Delete Booking
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default BookingDetailsSidebar;
